import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  FetchProductsResponse,
  GenerateAnalyticReportBodyType,
  ItemToCart,
  NomenclatureFilterType,
  DMSAnalyticsSliceState,
  ProductsFilterItemType,
  ProductBodyType,
  NomenclatureSearchFilterType,
} from "./type";
import { requestServise } from "@/shared";
import { revertAll } from "@/app/model/actions";
import { PageSizeType } from "@/app/model/types";

const initialState: DMSAnalyticsSliceState = {
  filters: [
    {
      type: "",
      fields: {},
    },
  ],
  selectedFilters: {},
  error: null,
  selectedAnalyticProductsIds: [],
  sortRules: {
    type: "desc",
    field: "VendorCode",
  },
  allIds: [],
  hideProductColumnsList: [],
  isLoading: false,
  isFilterLoading: false,
  itemsToCart: [],
  searchCode: null,
  pageSize: (+localStorage.getItem("psizeOA") as PageSizeType) || 20,
  totalCount: 0,
  excludeItems: [],
  filalsIdToShow: [],
  analyticItems: [],
  selectedDates: [],
  isReportGenerated: false,
};

export const generateOrderAnalytics = requestServise.post<
  unknown,
  GenerateAnalyticReportBodyType
>("dmsanalytic/generateOrderAnalytics", "/dmsanalytic/generate");

export const fetchFilters = requestServise.post<
  { filters: NomenclatureFilterType },
  {
    filters: {
      type: string;
      fields?: ProductsFilterItemType | NomenclatureSearchFilterType | [];
      params?: { dealerId: string };
    }[];
  }
>("dmsanalytic/fetchFilters", "/filters/dmsanalytic");

export const getAnalyticProducts = requestServise.post<
  FetchProductsResponse,
  ProductBodyType
>("dmsanalytic/getAnalyticProducts", "/dmsanalytic/grouped-by-vendor");

export const dmsanalyticSlice = createSlice({
  name: "dmsanalytic",
  initialState,
  reducers: {
    removeProducts() {
      return initialState;
    },
    updateProduct(state, action) {
      return (state = { ...state, ...action.payload });
    },

    clearFilters(state) {
      state.selectedFilters = initialState.selectedFilters;
    },
    setSelectedAnalyticsFilters(state, action) {
      state.selectedFilters = action.payload;
      state.searchCode = null;
    },
    setSelectedAnalyticProductsIds(state, action: PayloadAction<string>) {
      if (state.selectedAnalyticProductsIds.includes(action.payload)) {
        state.selectedAnalyticProductsIds =
          state.selectedAnalyticProductsIds.filter(
            (id) => id !== action.payload,
          );
      } else {
        state.selectedAnalyticProductsIds.push(action.payload);
      }
    },
    clearSelectedProductsId(state) {
      state.selectedAnalyticProductsIds =
        initialState.selectedAnalyticProductsIds;
    },
    setSelectedAllRows(state, action: PayloadAction<string[]>) {
      const check = action.payload.length === state.allIds.length;

      if (check) {
        state.selectedAnalyticProductsIds = [];
      } else {
        state.selectedAnalyticProductsIds = state.allIds;
      }
    },
    setProductColumnsToHide(state, action) {
      state.hideProductColumnsList = action.payload;
    },

    setItemsToCart(state, action: PayloadAction<ItemToCart[]>) {
      state.itemsToCart = action.payload;
    },

    setSortRules(state, action: PayloadAction<typeof state.sortRules>) {
      state.sortRules = action.payload;
    },
    setSearchCode(state, action: PayloadAction<typeof state.searchCode>) {
      state.searchCode = action.payload;
      state.selectedFilters = {};
    },
    setPageSize(state, action: PayloadAction<typeof state.pageSize>) {
      state.pageSize = action.payload;
    },
    changeFilialsIdToShow(state, action: PayloadAction<string>) {
      const id = action.payload;
      if (state.filalsIdToShow.includes(id)) {
        state.filalsIdToShow = state.filalsIdToShow.filter((i) => i !== id);
      } else {
        state.filalsIdToShow.push(id);
      }
    },
    changeAllFilialsId(state) {
      state.filalsIdToShow.length === state.allIds.length
        ? (state.filalsIdToShow = [])
        : (state.filalsIdToShow = state.allIds);
    },
    setSelectedDates(state, action) {
      state.selectedDates = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(getAnalyticProducts.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });

    builder.addCase(
      getAnalyticProducts.fulfilled,
      (state, action: PayloadAction<FetchProductsResponse>) => {
        const res = action.payload;

        state.analyticItems = action.payload?.items;
        const ids = res.items?.reduce((acc, product) => {
          if (product.canOrdered) {
            acc = [...acc, product.vendorId];
          }
          return acc;
        }, []);
        state.allIds = ids;
        state.totalCount = res.totalCount;
        state.selectedAnalyticProductsIds = [];
        state.isLoading = false;
      },
    );

    builder.addCase(getAnalyticProducts.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addCase(generateOrderAnalytics.pending, (state) => {
      state.error = null;
      state.isLoading = true;
      state.isReportGenerated = false;
    });
    builder.addCase(generateOrderAnalytics.fulfilled, (state) => {
      state.isLoading = false;
      state.isReportGenerated = true;
      state.error = null;
    });
    builder.addCase(generateOrderAnalytics.rejected, (state, action) => {
      state.isLoading = false;
      state.isReportGenerated = false;
      state.error = action.payload;
    });

    builder.addCase(fetchFilters.pending, (state) => {
      state.isFilterLoading = true;
      state.error = null;
    });

    builder.addCase(
      fetchFilters.fulfilled,
      (state, action: PayloadAction<{ filters: NomenclatureFilterType }>) => {
        state.filters = action.payload.filters;
        state.isFilterLoading = false;
      },
    );

    builder.addCase(fetchFilters.rejected, (state, action) => {
      state.isFilterLoading = false;
      state.error = action.payload;
    });
  },
});

export const {
  removeProducts,
  updateProduct,
  clearFilters,
  setSelectedAnalyticsFilters,
  setSelectedAnalyticProductsIds,
  setSelectedAllRows,
  setProductColumnsToHide,
  setItemsToCart,
  setSortRules,
  clearSelectedProductsId,
  setSearchCode,
  setPageSize,
  changeAllFilialsId,
  changeFilialsIdToShow,
  setSelectedDates,
} = dmsanalyticSlice.actions;
